import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { endOfDay, format } from "date-fns"
import { CalendarIcon, Search, X } from "lucide-react"
import * as React from "react"
import type { DateRange } from "react-day-picker"
import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover"
import { QuickFilterToggle } from "@/features/offers-admin/QuickFilterToggle"
import { TransactionsAdminGrid } from "@/features/transactions-admin/TransactionsAdminGrid"
import type { TransactionsAdminInput } from "@/features/transactions-admin/transactions-admin-datasource"
import { useDebounce } from "@/hooks/use-debounce"
import type { AdminTransactionFiltersInput } from "@/types/marketplace.types"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/transactions/")({
	component: TransactionsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePage(search.page),
		// Column-managed filters (CSV-encoded, owned by the grid).
		status: search.status as string | undefined,
		region: search.region as string | undefined,
		riskLevel: search.riskLevel as string | undefined,
		dispute: search.dispute as string | undefined,
		// Non-column
		search: search.search as string | undefined,
		dateFrom: normalizeDateString(search.dateFrom),
		dateTo: normalizeDateString(search.dateTo),
		sort: search.sort as string | undefined,
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

function normalizeDateString(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined
	const d = new Date(raw)
	return Number.isNaN(d.getTime()) ? undefined : raw
}

function DebouncedSearchInput({
	defaultValue,
	onDebouncedChange,
	inputRef,
}: {
	defaultValue: string
	onDebouncedChange: (value: string) => void
	inputRef?: React.RefObject<{ clear: () => void } | null>
}) {
	const [value, setValue] = React.useState(defaultValue)
	const debouncedValue = useDebounce(value, 150)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
			return
		}
		onDebouncedChange(debouncedValue)
	}, [debouncedValue, onDebouncedChange])

	React.useImperativeHandle(inputRef, () => ({
		clear: () => setValue(""),
	}))

	return (
		<div className="relative w-[260px] shrink-0">
			<Search className="absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-meta" />
			<Input
				placeholder="TX number, Stripe id, @user"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="pl-8"
			/>
		</div>
	)
}

function TransactionsPage() {
	const searchParams = useSearch({ from: "/transactions/" })
	const navigate = useNavigate()

	const pageIndex = (searchParams.page ?? 1) - 1
	const statusFilter = searchParams.status ?? ""
	const riskLevelFilter = searchParams.riskLevel ?? ""
	const disputeFilter = searchParams.dispute ?? ""

	const dateRange: DateRange | undefined = React.useMemo(() => {
		if (!searchParams.dateFrom && !searchParams.dateTo) return undefined
		return {
			from: searchParams.dateFrom ? new Date(searchParams.dateFrom) : undefined,
			to: searchParams.dateTo ? new Date(searchParams.dateTo) : undefined,
		}
	}, [searchParams.dateFrom, searchParams.dateTo])

	const searchRef = React.useRef<{ clear: () => void }>(null)

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/transactions",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === "all" || val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: value || undefined, page: undefined })
		},
		[updateFilters],
	)

	const [sortBy, sortOrder] = React.useMemo<
		[
			AdminTransactionFiltersInput["sortBy"],
			AdminTransactionFiltersInput["sortOrder"],
		]
	>(() => {
		if (searchParams.sort) {
			const [field, direction] = searchParams.sort.split(":")
			const validFields = [
				"createdAt",
				"total",
				"transactionNumber",
				"riskLevel",
			] as const
			if (
				validFields.includes(field as (typeof validFields)[number]) &&
				(direction === "asc" || direction === "desc")
			) {
				return [
					field as AdminTransactionFiltersInput["sortBy"],
					direction as AdminTransactionFiltersInput["sortOrder"],
				]
			}
		}
		return ["createdAt", "desc"]
	}, [searchParams.sort])

	// Only non-column filters reach the grid via `input`. Column filters
	// (status / region / riskLevel / dispute) come from the URL and are
	// applied via initialState + the grid's URL-sync effect.
	const gridInput = React.useMemo<TransactionsAdminInput>(() => {
		const filters: TransactionsAdminInput = {
			sortBy,
			sortOrder,
		}
		if (searchParams.search) filters.search = searchParams.search
		if (searchParams.dateFrom) filters.dateFrom = searchParams.dateFrom
		if (searchParams.dateTo) filters.dateTo = searchParams.dateTo
		return filters
	}, [
		searchParams.search,
		searchParams.dateFrom,
		searchParams.dateTo,
		sortBy,
		sortOrder,
	])

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(
			nextSortBy: AdminTransactionFiltersInput["sortBy"],
			nextSortOrder: AdminTransactionFiltersInput["sortOrder"],
		) => {
			const isDefault = nextSortBy === "createdAt" && nextSortOrder === "desc"
			updateFilters({
				sort: isDefault ? undefined : `${nextSortBy}:${nextSortOrder}`,
				page: undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		navigate({ to: "/transactions", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters =
		!!searchParams.status ||
		!!searchParams.region ||
		!!searchParams.riskLevel ||
		!!searchParams.dispute ||
		dateRange !== undefined ||
		!!searchParams.search

	const toggleChip = React.useCallback(
		(key: "status" | "riskLevel" | "dispute", value: string) => {
			const current = searchParams[key]
			updateFilters({
				[key]: current === value ? undefined : value,
				page: undefined,
			})
		},
		[searchParams, updateFilters],
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader title="Transactions" />

			<div className="min-h-0 flex-1">
				<TransactionsAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					onSortChange={handleSortChange}
					toolbar={
						<>
							<DebouncedSearchInput
								defaultValue={searchParams.search ?? ""}
								onDebouncedChange={handleDebouncedSearch}
								inputRef={searchRef}
							/>

							<Popover>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										className={cn(!dateRange && "text-muted-foreground")}
									>
										<CalendarIcon />
										{dateRange?.from ? (
											dateRange.to ? (
												<>
													{format(dateRange.from, "LLL dd")} to{" "}
													{format(dateRange.to, "LLL dd")}
												</>
											) : (
												format(dateRange.from, "LLL dd, y")
											)
										) : (
											<span>Date range</span>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-auto p-0" align="start">
									<Calendar
										initialFocus
										mode="range"
										defaultMonth={dateRange?.from}
										selected={dateRange}
										onSelect={(range) =>
											updateFilters({
												dateFrom: range?.from?.toISOString() || undefined,
												dateTo: range?.to
													? endOfDay(range.to).toISOString()
													: undefined,
												page: undefined,
											})
										}
										numberOfMonths={2}
									/>
								</PopoverContent>
							</Popover>

							<QuickFilterToggle
								active={statusFilter === "failed"}
								onToggle={() => toggleChip("status", "failed")}
							>
								Failed
							</QuickFilterToggle>
							<QuickFilterToggle
								active={riskLevelFilter === "critical"}
								onToggle={() => toggleChip("riskLevel", "critical")}
							>
								Critical risk
							</QuickFilterToggle>
							<QuickFilterToggle
								active={statusFilter === "refunded"}
								onToggle={() => toggleChip("status", "refunded")}
							>
								Refunded
							</QuickFilterToggle>
							<QuickFilterToggle
								active={disputeFilter === "has"}
								onToggle={() => toggleChip("dispute", "has")}
							>
								Chargebacks
							</QuickFilterToggle>

							{hasActiveFilters && (
								<Button variant="ghost" onClick={clearFilters}>
									<X />
									Clear
								</Button>
							)}
						</>
					}
				/>
			</div>
		</div>
	)
}
